import React, { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import { NumericFormat } from 'react-number-format';

const InlineInputText = ({ text, onInputChange, inputFields, values }) => {
  const [activeInput, setActiveInput] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleKeyDown = (event, key) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      setActiveInput(null);
    }
  };

  const renderTextWithInputs = () => {
    const parts = text.split(/(\{\w+\})/g);
    return parts.map((part, index) => {
      if (part.match(/^\{(\w+)\}$/)) {
        const key = part.slice(1, -1);
        const field = inputFields[key];

        if (!field) return part;

        switch (field.type) {
          case 'select':
            return renderSelect(key, field, index);
          case 'currency':
            return renderCurrency(key, field, index);
          case 'percentage':
          case 'number':
            return renderNumber(key, field, index);
          default:
            return renderText(key, field, index);
        }
      }
      return <span key={index}>{part}</span>;
    });
  };

  const renderSelect = (key, field, index) => {
    const value = field.options.find(option => option.value === values[key]) || null;
    return (
      <span key={index} className="inline-block">
        {activeInput !== key ? (
          <span 
            className="border-b-2 border-green-500 px-1 cursor-pointer hover:bg-green-100"
            onClick={() => setActiveInput(key)}
          >
            {value ? value.label : 'Select an option'}
          </span>
        ) : (
          <Select
            options={field.options}
            value={value}
            onChange={(selectedOption) => {
              handleInputChange(key, selectedOption.value);
              setActiveInput(null);
            }}
            className="inline-block w-48 mx-1"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '30px',
                height: '30px',
                border: 'none',
                borderBottom: '2px solid #e2e8f0',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: '#4a5568'
                }
              }),
              valueContainer: (base) => ({
                ...base,
                height: '30px',
                padding: '0 6px',
              }),
              input: (base) => ({
                ...base,
                margin: '0px',
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: '30px',
              }),
              placeholder: (base) => ({
                ...base,
                color: '#4a5568',
              }),
            }}
            placeholder="Select an option"
            isSearchable={false}
          />
        )}
      </span>
    );
  };

  const renderCurrency = (key, field, index) => (
    <span key={index} className="inline-block" ref={activeInput === key ? inputRef : null}>
      {activeInput !== key ? (
        <span 
          className="border-b-2 border-green-500 px-1 cursor-pointer hover:bg-green-100"
          onClick={() => setActiveInput(key)}
        >
          ${Number(values[key] || 0).toLocaleString()}
        </span>
      ) : (
        <NumericFormat
          value={values[key]}
          onValueChange={(v) => handleInputChange(key, v.value)}
          thousandSeparator={true}
          prefix={'$'}
          className="inline-block w-32 px-1 py-0 mx-1 border-b-2 border-gray-300 focus:border-green-500 focus:outline-none"
          inputMode="decimal"
          onKeyDown={(e) => handleKeyDown(e, key)}
        />
      )}
    </span>
  );

  const renderNumber = (key, field, index) => (
    <span key={index} className="inline-block" ref={activeInput === key ? inputRef : null}>
      {activeInput !== key ? (
        <span 
          className="border-b-2 border-green-500 px-1 cursor-pointer hover:bg-green-100"
          onClick={() => setActiveInput(key)}
        >
          {Number(values[key] || 0).toFixed(field.type === 'percentage' ? 2 : 0)}

        </span>
      ) : (
        <NumericFormat
          value={values[key]}
          onValueChange={(v) => handleInputChange(key, v.value)}
          decimalScale={field.type === 'percentage' ? 2 : 0}
          fixedDecimalScale={field.type === 'percentage'}
          allowNegative={false}
          className="inline-block w-20 px-1 py-0 mx-1 border-b-2 border-gray-300 focus:border-green-500 focus:outline-none"
          inputMode="decimal"
          onKeyDown={(e) => handleKeyDown(e, key)}
          getInputRef={(el) => {
            if (el) {
              el.style.lineHeight = 'inherit';
              el.style.height = 'auto';
            }
          }}
        />
      )}
    </span>
  );

  const renderText = (key, field, index) => (
    <input
      key={index}
      type="text"
      value={values[key] || ''}
      onChange={(e) => handleInputChange(key, e.target.value)}
      className="inline-block w-32 px-1 py-0 mx-1 border-b-2 border-gray-300 focus:border-green-500 focus:outline-none"
      onKeyDown={(e) => handleKeyDown(e, key)}
    />
  );

  const handleInputChange = (key, value) => {
    onInputChange({ [key]: value });
  };

  return (
    <div className="text-base sm:text-xl leading-relaxed">
      {renderTextWithInputs()}
    </div>
  );
};

export default InlineInputText;